import React from 'react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { TAXONOMY_CODES } from './constants';

export const FieldError = ({ errors, field }) =>
  errors[field] ? <p className="text-xs text-red-500 mt-1">{errors[field]}</p> : null;

// --- Step 1: Agency Info -------------------------------------------------
export const AgencyInfoStep = ({ form, set, setField, errors }) => (
  <div className="space-y-4" data-testid="step-1-form">
    <div className="space-y-1.5">
      <Label className="text-xs font-medium">Agency Legal Name *</Label>
      <Input value={form.agency_name} onChange={set('agency_name')} placeholder="e.g. Comfort & Passionate Health Care" data-testid="input-agency-name" />
      <FieldError errors={errors} field="agency_name" />
    </div>
    <div className="space-y-1.5">
      <Label className="text-xs font-medium">DBA Name <span className="text-slate-400">(if different)</span></Label>
      <Input value={form.dba_name} onChange={set('dba_name')} placeholder="Doing business as..." data-testid="input-dba-name" />
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-1.5">
        <Label className="text-xs font-medium">NPI * <span className="text-slate-400">(10 digits)</span></Label>
        <Input value={form.npi} onChange={set('npi')} placeholder="1234567890" maxLength={10} data-testid="input-npi" />
        <FieldError errors={errors} field="npi" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-medium">Tax ID / EIN *</Label>
        <Input value={form.tax_id} onChange={set('tax_id')} placeholder="XX-XXXXXXX" data-testid="input-tax-id" />
        <FieldError errors={errors} field="tax_id" />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-1.5">
        <Label className="text-xs font-medium">Medicaid Provider ID</Label>
        <Input value={form.medicaid_provider_id} onChange={set('medicaid_provider_id')} placeholder="ODM provider ID" data-testid="input-medicaid-id" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-medium">Taxonomy Code</Label>
        <Select value={form.taxonomy_code} onValueChange={(v) => setField('taxonomy_code', v)}>
          <SelectTrigger data-testid="input-taxonomy"><SelectValue placeholder="Select taxonomy" /></SelectTrigger>
          <SelectContent>
            {TAXONOMY_CODES.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {form.taxonomy_code === 'other' && (
          <Input value={form.taxonomy_other} onChange={set('taxonomy_other')} placeholder="Enter taxonomy code" className="mt-2" data-testid="input-taxonomy-other" />
        )}
      </div>
    </div>
  </div>
);

// --- Step 2: Sandata EVV -------------------------------------------------
export const SandataStep = ({ form, set }) => (
  <div className="space-y-4" data-testid="step-2-form">
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
      <p className="text-xs text-blue-800">These fields are optional. If you don't have Sandata credentials yet, AZAI can configure them for you after approval.</p>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-1.5">
        <Label className="text-xs font-medium">Sandata Provider ID</Label>
        <Input value={form.sandata_provider_id} onChange={set('sandata_provider_id')} placeholder="HHAExchange provider ID" data-testid="input-sandata-provider-id" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-medium">Sandata Provider Code</Label>
        <Input value={form.sandata_provider_code} onChange={set('sandata_provider_code')} placeholder="e.g. 42407" data-testid="input-sandata-code" />
      </div>
    </div>
    <div className="space-y-1.5">
      <Label className="text-xs font-medium">Sandata Medicaid ID</Label>
      <Input value={form.sandata_medicaid_id} onChange={set('sandata_medicaid_id')} placeholder="12-digit Medicaid ID for Sandata" data-testid="input-sandata-medicaid" />
    </div>
    <div className="border-t pt-4 mt-4">
      <p className="text-xs text-slate-500 mb-3">API Credentials (optional — only needed if your agency has its own Sandata account)</p>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">API Username</Label>
          <Input value={form.sandata_api_username} onChange={set('sandata_api_username')} placeholder="API username" data-testid="input-sandata-user" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">API Password</Label>
          <Input type="password" value={form.sandata_api_password} onChange={set('sandata_api_password')} placeholder="API password" data-testid="input-sandata-pass" />
        </div>
      </div>
    </div>
  </div>
);

// --- Step 3: Address & Contact ------------------------------------------
export const AddressContactStep = ({ form, set, errors }) => (
  <div className="space-y-4" data-testid="step-3-form">
    <div className="space-y-1.5">
      <Label className="text-xs font-medium">Street Address *</Label>
      <Input value={form.street1} onChange={set('street1')} placeholder="123 Main Street" data-testid="input-street1" />
      <FieldError errors={errors} field="street1" />
    </div>
    <div className="space-y-1.5">
      <Label className="text-xs font-medium">Street Address Line 2</Label>
      <Input value={form.street2} onChange={set('street2')} placeholder="Suite, Apt, etc." data-testid="input-street2" />
    </div>
    <div className="grid grid-cols-3 gap-3">
      <div className="space-y-1.5">
        <Label className="text-xs font-medium">City *</Label>
        <Input value={form.city} onChange={set('city')} placeholder="Columbus" data-testid="input-city" />
        <FieldError errors={errors} field="city" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-medium">State</Label>
        <Input value={form.state} onChange={set('state')} disabled className="bg-slate-50" data-testid="input-state" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-medium">Zip Code *</Label>
        <Input value={form.zip_code} onChange={set('zip_code')} placeholder="43215" maxLength={5} data-testid="input-zip" />
        <FieldError errors={errors} field="zip_code" />
      </div>
    </div>
    <div className="space-y-1.5">
      <Label className="text-xs font-medium">County</Label>
      <Input value={form.county} onChange={set('county')} placeholder="e.g. Franklin" data-testid="input-county" />
    </div>
    <div className="border-t pt-4 mt-2">
      <h4 className="text-sm font-semibold text-slate-700 mb-3">Primary Contact</h4>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Contact Name *</Label>
          <Input value={form.contact_name} onChange={set('contact_name')} placeholder="Full name" data-testid="input-contact-name" />
          <FieldError errors={errors} field="contact_name" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Title</Label>
          <Input value={form.contact_title} onChange={set('contact_title')} placeholder="e.g. Director of Nursing" data-testid="input-contact-title" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Phone *</Label>
          <Input value={form.contact_phone} onChange={set('contact_phone')} placeholder="614-555-1234" data-testid="input-contact-phone" />
          <FieldError errors={errors} field="contact_phone" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Email *</Label>
          <Input value={form.contact_email} onChange={set('contact_email')} placeholder="contact@agency.com" data-testid="input-contact-email" />
          <FieldError errors={errors} field="contact_email" />
        </div>
      </div>
      <div className="mt-3 space-y-1.5">
        <Label className="text-xs font-medium">Fax</Label>
        <Input value={form.contact_fax} onChange={set('contact_fax')} placeholder="Optional" data-testid="input-fax" />
      </div>
    </div>
  </div>
);

// --- Step 4: Admin Account -----------------------------------------------
export const AdminAccountStep = ({ form, set, errors }) => (
  <div className="space-y-4" data-testid="step-4-form">
    <div className="space-y-1.5">
      <Label className="text-xs font-medium">Your Full Name *</Label>
      <Input value={form.admin_name} onChange={set('admin_name')} placeholder="e.g. Jane Smith" data-testid="input-admin-name" />
      <FieldError errors={errors} field="admin_name" />
    </div>
    <div className="space-y-1.5">
      <Label className="text-xs font-medium">Email Address *</Label>
      <Input type="email" value={form.admin_email} onChange={set('admin_email')} placeholder="you@agency.com" data-testid="input-admin-email" />
      <FieldError errors={errors} field="admin_email" />
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-1.5">
        <Label className="text-xs font-medium">Password *</Label>
        <Input type="password" value={form.admin_password} onChange={set('admin_password')} placeholder="Min 8 chars" data-testid="input-admin-password" />
        <FieldError errors={errors} field="admin_password" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-medium">Confirm Password *</Label>
        <Input type="password" value={form.admin_password_confirm} onChange={set('admin_password_confirm')} placeholder="Confirm" data-testid="input-admin-password-confirm" />
        <FieldError errors={errors} field="admin_password_confirm" />
      </div>
    </div>
    <p className="text-xs text-slate-400">Password must be 8+ characters with uppercase, lowercase, and a digit.</p>
  </div>
);
